import { useId, type HTMLAttributes } from 'react'
import { cn } from '../lib/cn'

/**
 * Evolução de um número no tempo: barras em fio, uma por ponto, com a série anterior atrás em alto
 * para comparar. Sem biblioteca de gráfico: é CSS e tokens. O desenho é `role="img"` com o nome
 * dado; os valores inteiros ficam numa tabela só para leitores de tela. Só o que representa risco
 * ganha plum (`tone="critical"`); o resto segue ink.
 */

export interface EvolutionPoint {
  /** O rótulo do eixo: "08/10", "sem 2". */
  label: string
  value: number
  /** O mesmo ponto no período anterior; `null` ou ausente quando não há comparação. */
  previous?: number | null
}

export interface EvolutionChartProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  points: EvolutionPoint[]
  /** Nome acessível do gráfico e legenda da tabela ("Valor gerado por dia"). */
  label: string
  /** Formata o valor para o teto e a tabela ("R$ 3.000", "12"). Padrão: o número. */
  format?: (value: number) => string
  /** Rótulo da série anterior na legenda; ausente, a legenda não aparece. */
  previousLabel?: string
  tone?: 'default' | 'critical'
  /** Altura do desenho, em px. */
  height?: number
  /** Quantos rótulos do eixo aparecem no máximo (o primeiro e o último sempre). */
  maxAxisLabels?: number
  /** O que dizer quando todos os valores são zero. */
  emptyNote?: string
}

function axisIndexes(count: number, max: number): Set<number> {
  if (count <= max) return new Set(Array.from({ length: count }, (_, i) => i))
  const out = new Set<number>([0, count - 1])
  const step = (count - 1) / Math.max(1, max - 1)
  for (let i = 1; i < max - 1; i++) out.add(Math.round(i * step))
  return out
}

export function EvolutionChart({
  points,
  label,
  format = (v) => String(v),
  previousLabel,
  tone = 'default',
  height = 160,
  maxAxisLabels = 8,
  emptyNote = 'Sem valores no período.',
  className,
  ...props
}: EvolutionChartProps) {
  const id = useId()
  const hasPrevious = points.some((p) => typeof p.previous === 'number')
  const max = Math.max(0, ...points.map((p) => Math.max(p.value, p.previous ?? 0)))
  const isEmpty = max === 0
  const shown = axisIndexes(points.length, maxAxisLabels)
  const barClass = tone === 'critical' ? 'bg-plum' : 'bg-ink'

  return (
    <div
      data-slot="evolution-chart"
      data-tone={tone}
      className={cn('flex flex-col gap-2', className)}
      {...props}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-body text-eyebrow text-label leading-[1.4] font-light tracking-[.12em] uppercase">
          {isEmpty ? emptyNote : `máx. ${format(max)}`}
        </span>
        {hasPrevious && previousLabel ? (
          <span className="font-body text-eyebrow text-secondary flex items-center gap-3 leading-[1.4] font-light tracking-normal">
            <span className="flex items-center gap-1">
              <span aria-hidden="true" className={cn('inline-block h-[8px] w-[8px]', barClass)} />
              {label}
            </span>
            <span className="flex items-center gap-1">
              <span aria-hidden="true" className="bg-alto inline-block h-[8px] w-[8px]" />
              {previousLabel}
            </span>
          </span>
        ) : null}
      </div>

      <div role="img" aria-label={label} aria-describedby={`${id}-table`}>
        <div className="border-hairline flex items-end gap-[2px] border-b" style={{ height }}>
          {points.map((point, index) => {
            const valueHeight = isEmpty ? 0 : (point.value / max) * 100
            const previousHeight =
              isEmpty || typeof point.previous !== 'number' ? 0 : (point.previous / max) * 100
            return (
              <div
                key={`${point.label}-${index}`}
                className="relative flex h-full flex-1 items-end justify-center"
              >
                {hasPrevious ? (
                  <div
                    aria-hidden="true"
                    className="bg-alto absolute bottom-0 w-[60%]"
                    style={{ height: `${previousHeight}%`, minHeight: previousHeight > 0 ? 2 : 0 }}
                  />
                ) : null}
                <div
                  aria-hidden="true"
                  className={cn('relative w-[60%]', barClass)}
                  style={{ height: `${valueHeight}%`, minHeight: point.value > 0 ? 2 : 0 }}
                />
              </div>
            )
          })}
        </div>
        <div className="mt-1 flex gap-[2px]">
          {points.map((point, index) => (
            <div
              key={`${point.label}-${index}`}
              className="font-body text-eyebrow text-label flex-1 truncate text-center leading-[1.4] font-light tracking-normal"
              aria-hidden="true"
            >
              {shown.has(index) ? point.label : ''}
            </div>
          ))}
        </div>
      </div>

      <table id={`${id}-table`} className="sr-only">
        <caption>{label}</caption>
        <thead>
          <tr>
            <th scope="col">Ponto</th>
            <th scope="col">Valor</th>
            {hasPrevious ? <th scope="col">{previousLabel ?? 'Anterior'}</th> : null}
          </tr>
        </thead>
        <tbody>
          {points.map((point, index) => (
            <tr key={`${point.label}-${index}`}>
              <th scope="row">{point.label}</th>
              <td>{format(point.value)}</td>
              {hasPrevious ? (
                <td>{typeof point.previous === 'number' ? format(point.previous) : '—'}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
